import type { FormEvent } from 'react'
import type { RegistrationFormElement } from 'types'
import type { Demand, DemandInputName, DemandEditFormData, ClientDemand } from 'types/buyer'

import { useState, useEffect } from 'react'
import InputMask from 'react-input-mask'

import { useStore } from 'store'
import { fiveYearsFromNow } from 'common'
import { getInput, getValue, leaveOnlyNumbers, trimFormData } from 'utils'

import ModalContent from 'components/ModalContent'
import Button from 'components/Button'
import InputLabel from 'components/InputLabel'
import Input, { PrefixedInput } from 'components/Input'
import { validateDemandForm } from 'utils/pageValidators'
import Spinner from 'components/Spinner'
import InfoTooltip from 'components/InfoTooltip'
import StyledModal from 'components/layout/StyledModal'
import copy from 'copy.json'

const button = copy.common.button
const text = copy.buyer.add_demand
const form_text = copy.common.form

type Props = {
  demandData: Demand | ClientDemand
  isModalOpen: boolean
  onModalClose: () => void
  refreshData: () => void
}

export default function EditDemandDetailsModal({
  demandData,
  isModalOpen,
  onModalClose,
  refreshData
}: Props) {
  const { api, fieldErrors, clearFieldErrors } = useStore()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    [key in DemandInputName]?: string
  }>({})

  const isClientData = !('id' in demandData)

  const getCity = async (zipCode: string) => {
    const input: HTMLInputElement | null = document.querySelector('[name="location_city"]')
    const city = await api.getCity(zipCode, isClientData)

    if (!input || !city) return

    input.value = city
    setErrors(state => ({ ...state, location_city: undefined }))
  }

  const closeModal = () => {
    onModalClose()
    clearFieldErrors()
  }

  const validateInput = (
    event?: FormEvent<HTMLInputElement>,
    element?: HTMLInputElement | null
  ) => {
    const input = element || event?.currentTarget
    if (!input) return
    const validation = validateDemandForm(input, true)
    setErrors(state => ({ ...state, [input.name]: validation }))
    return validation
  }

  const submit = async (event: FormEvent<RegistrationFormElement>) => {
    event.preventDefault()
    setLoading(true)

    const form = event.currentTarget

    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateDemandForm(input, true) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) {
      setErrors(errors)
      setLoading(false)
      return
    }

    const requestData = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({
        ...result,
        [name]: value === '' || value === '0' ? null : value
      }),
      {} as DemandEditFormData
    )

    requestData.yearly_power_consumption = parseFloat(String(requestData.yearly_power_consumption))
    requestData.contract_length = parseFloat(String(requestData.contract_length))
    requestData.energy_multiplier = parseFloat(String(requestData.energy_multiplier))
    requestData.current_energy_price = parseFloat(String(requestData.current_energy_price))
    requestData.location_zip_code = leaveOnlyNumbers(requestData.location_zip_code)
    requestData.current_contract_end_date = requestData.current_contract_end_date || null

    requestData.version = demandData.version

    const demandId = isClientData ? demandData.business_id : demandData.id.toString()

    const data = await api.editDemand(demandId, isClientData, requestData)

    if (!data) {
      setLoading(false)
      return
    }

    setLoading(false)
    data === 'IS_CONFLICT' ? form.reset() : closeModal()
    refreshData()
  }

  useEffect(() => {
    setLoading(false)
  }, [])

  useEffect(() => {
    setErrors(fieldErrors)
  }, [fieldErrors])

  return (
    <StyledModal isOpen={isModalOpen} onClose={closeModal}>
      <ModalContent as="form" onSubmit={submit}>
        <h3>{copy.buyer.demand_details.modal.edit_demand.title}</h3>

        <InputLabel>
          <span>{form_text.osd_name}</span>
          <span>{errors.osd_name}</span>
          <Input name="osd_name" defaultValue={demandData.osd_name} onBlur={validateInput} />
        </InputLabel>

        <InputLabel>
          <span>{form_text.electric_energy_meter_number}</span>
          <span>{errors.electric_energy_meter_number}</span>
          <Input
            name="electric_energy_meter_number"
            defaultValue={demandData.electric_energy_meter_number}
            onBlur={validateInput}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.energy_point_number}</span>
          <span>{errors.energy_point_number}</span>
          <Input
            name="energy_point_number"
            defaultValue={demandData.energy_point_number}
            onBlur={validateInput}
          />
        </InputLabel>

        <InputLabel>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {form_text.energy_tariff_group}
            <InfoTooltip>{form_text.energy_tariff_group_hint}</InfoTooltip>
          </span>
          <span>{errors.energy_tariff_group}</span>
          <Input
            name="energy_tariff_group"
            defaultValue={demandData.energy_tariff_group}
            onBlur={validateInput}
          />
        </InputLabel>

        <InputLabel>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {form_text.energy_multiplier}
            <InfoTooltip>{form_text.energy_multiplier_hint}</InfoTooltip>
          </span>
          <span>{errors.energy_multiplier}</span>
          <PrefixedInput
            name="energy_multiplier"
            type="number"
            defaultValue={demandData.energy_multiplier}
            step={1}
            min={1}
            max={1000000}
            prefix="x"
            prefixWidth="25px"
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.yearly_power_consumption}</span>
          <span>{errors.yearly_power_consumption}</span>
          <PrefixedInput
            name="yearly_power_consumption"
            defaultValue={demandData.yearly_power_consumption}
            type="number"
            prefix={copy.common.MWh}
            prefixWidth="50px"
            reverse
            step={1}
            min={1}
            max={1000000}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.current_energy_price}</span>
          <span>{errors.current_energy_price}</span>
          <PrefixedInput
            name="current_energy_price"
            defaultValue={demandData.current_energy_price?.toFixed(2)}
            type="number"
            prefix={copy.common.zl}
            prefixWidth="28px"
            reverse
            min={1}
            step={0.01}
            max={10000}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{text.form.current_energy_provider_name}</span>
          <span>{errors.current_energy_provider_name}</span>
          <Input
            name="current_energy_provider_name"
            defaultValue={demandData.current_energy_provider_name}
            onBlur={validateInput}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.current_contract_end_date}</span>
          <span>{errors.current_contract_end_date}</span>
          <Input
            type="date"
            name="current_contract_end_date"
            defaultValue={demandData.current_contract_end_date || ''}
            min="1970-01-01"
            max={fiveYearsFromNow}
            onBlur={event => {
              validateInput(event)
              getValue('contract_start_date') &&
                validateInput(undefined, getInput('contract_start_date'))
            }}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.contract_start_date}</span>
          <span>{errors.contract_start_date}</span>
          <Input
            type="date"
            name="contract_start_date"
            defaultValue={demandData.contract_start_date}
            min="1970-01-01"
            max={fiveYearsFromNow}
            onBlur={event => {
              validateInput(event)
              getValue('current_contract_end_date') &&
                validateInput(undefined, getInput('current_contract_end_date'))
            }}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{text.form.contract_length}</span>
          <span>{errors.contract_length}</span>
          <PrefixedInput
            type="number"
            name="contract_length"
            defaultValue={demandData.contract_length}
            prefix={copy.common.months_abbr}
            prefixWidth="52px"
            reverse
            min={1}
            max={240}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <h4>{text.subheading}</h4>

        <InputLabel>
          <span>{form_text.location_zip_code}</span>
          <span>{errors.location_zip_code}</span>
          <InputMask
            mask="99-999"
            maskPlaceholder={null}
            defaultValue={demandData.location_zip_code}
            onInput={event => !validateInput(event) && getCity(event.currentTarget.value)}
            onBlur={validateInput}
          >
            <Input name="location_zip_code" style={{ width: '50%' }} />
          </InputMask>
        </InputLabel>

        <InputLabel>
          <span>{form_text.location_city}</span>
          <span>{errors.location_city}</span>
          <Input
            name="location_city"
            defaultValue={demandData.location_city}
            onBlur={validateInput}
          />
        </InputLabel>

        <Button type="submit" disabled={loading}>
          {loading ? <Spinner size={16} /> : button.save}
        </Button>

        <Button type="button" secondary onClick={closeModal}>
          {button.cancel}
        </Button>
      </ModalContent>
    </StyledModal>
  )
}
